// ルーティング設定

import { Suspense, lazy } from 'react';
import { createBrowserRouter, RouteObject } from 'react-router-dom';
import Layout from '../components/Layout';
import { ProtectedRoute } from '../components/ProtectedRoute';
import Spinner from '../components/Spinner';
import { ROUTES } from '../constants/routes';

// ページはルート単位でコード分割し、初回ロードを軽くする
const HomePage = lazy(() => import('../pages/HomePage'));
const FileUploadPage = lazy(() => import('../pages/FileUploadPage'));
const CompanyInfoPage = lazy(() => import('../pages/CompanyInfoPage'));
const BCPCreatePage = lazy(() => import('../pages/BCPCreatePage'));
const IntroductoryBCPCreatePage = lazy(() => import('../pages/IntroductoryBCPCreatePage'));
const RestorePolicyPage = lazy(() => import('../pages/RestorePolicyPage'));
const BCPRegulationPage = lazy(() => import('../pages/BCPRegulationPage'));
const InitialResponsePage = lazy(() => import('../pages/InitialResponsePage'));
const ProcedureManualPage = lazy(() => import('../pages/ProcedureManualPage'));
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));
const AuthCallbackPage = lazy(() => import('../pages/AuthCallbackPage'));
const AuthErrorPage = lazy(() => import('../pages/AuthErrorPage'));
const SettingsPage = lazy(() => import('../pages/SettingsPage'));
const InquiryPage = lazy(() => import('../pages/InquiryPage'));
const TermsPage = lazy(() => import('../pages/TermsPage'));

const pageFallback = (
  <div className="min-h-screen flex items-center justify-center">
    <Spinner size="lg" centered ariaLabel="ページを読み込み中" />
  </div>
);

const withSuspense = (page: React.ReactNode) => (
  <Suspense fallback={pageFallback}>{page}</Suspense>
);

const protectedPage = (page: React.ReactNode) =>
  withSuspense(<ProtectedRoute>{page}</ProtectedRoute>);

// 統一ヘッダー(Layout)配下に置くページ
const layoutRoute = (path: string, page: React.ReactNode): RouteObject => ({
  path,
  element: protectedPage(<Layout />),
  children: [
    {
      index: true,
      element: withSuspense(page),
    },
  ],
});

// ルート定義
const routes: RouteObject[] = [
  // ホームページ（認証必須）
  {
    path: ROUTES.HOME,
    element: protectedPage(<HomePage />),
  },
  // 認証コールバック（認証不要）
  {
    path: '/auth/start',
    element: withSuspense(<AuthCallbackPage />),
  },
  // 認証エラー（認証不要）
  {
    path: ROUTES.AUTH_ERROR,
    element: withSuspense(<AuthErrorPage />),
  },
  // アップロードページ以降は統一ヘッダーを持つ
  layoutRoute(ROUTES.UPLOAD, <FileUploadPage />),
  layoutRoute(ROUTES.COMPANY_INFO, <CompanyInfoPage />),
  layoutRoute(ROUTES.SETTINGS, <SettingsPage />),
  layoutRoute(ROUTES.INQUIRY, <InquiryPage />),
  layoutRoute(ROUTES.TERMS, <TermsPage />),
  // BCP作成ページは独自のレイアウトを持つため、独立配置
  {
    path: ROUTES.CREATE_BCP,
    element: protectedPage(<BCPCreatePage />),
  },
  {
    path: ROUTES.CREATE_BCP_INTRO,
    element: protectedPage(<IntroductoryBCPCreatePage />),
  },
  {
    path: ROUTES.RESTORE_POLICY_SIMPLE,
    element: protectedPage(<RestorePolicyPage />),
  },
  {
    path: '/bcp/:bcpId/restore-policy',
    element: protectedPage(<RestorePolicyPage />),
  },
  {
    path: '/bcp/:bcpId/regulation',
    element: protectedPage(<BCPRegulationPage />),
  },
  {
    path: '/bcp/:bcpId/initial-response',
    element: protectedPage(<InitialResponsePage />),
  },
  {
    path: '/bcp/:bcpId/procedure-manual',
    element: protectedPage(<ProcedureManualPage />),
  },
  {
    path: '*',
    element: withSuspense(<NotFoundPage />),
  },
];

// ルーター作成
export const router = createBrowserRouter(routes);

// 後方互換のための再エクスポート（新規コードは constants/routes.ts を参照する）
export { ROUTES, type Route } from '../constants/routes';
